// Pure helpers for the Forecasting tab's over/understaffed heatmap (Stage 3,
// 2026-10-19). Kept out of ReportingPage.jsx so the logic is unit tested
// (ui/test/forecastHeatmap.test.js), same convention as scorecardsPooling.js.
//
// The report is `GET /api/admin/reporting/forecasting/heatmap`:
// { weekdays, hours, cells: [{ weekday, hour, open, phoneCalls, chatContacts,
//   workloadFte, staffedFte, spareFte, status }], summary, thresholds, ... }
// where status is 'short' | 'ok' | 'over' | null (null = closed hour, or no
// roster covers it).

export const HEATMAP_STATUS_LABEL = { short: 'Short', ok: 'OK', over: 'Over' }

// Diverging polarity, warm vs cool around a neutral middle: short staffing is
// the warm pole, over-staffing the cool one, "ok" carries no tint at all. The
// number in the cell is always shown, so colour is never the only signal.
// Values are MUI palette keys; the component turns them into tints.
export function heatmapStatusToken(status) {
  if (status === 'short') return 'error'
  if (status === 'over') return 'info'
  return null
}

// 'Mon|10:00' -> cell, for the grid's row-by-row lookups.
export function heatmapCellIndex(report) {
  return new Map((report?.cells ?? []).map((c) => [`${c.weekday}|${c.hour}`, c]))
}

// The hour rows worth drawing: any hour that is open on at least one weekday.
// An hour closed all week (overnight) would be a blank row.
export function heatmapHours(report) {
  const open = new Set((report?.cells ?? []).filter((c) => c.open).map((c) => c.hour))
  return (report?.hours ?? []).filter((h) => open.has(h))
}

// '10:00' -> '10 AM', '00:00' -> '12 AM', '13:00' -> '1 PM'.
export function heatmapHourLabel(hour) {
  const h = Number(String(hour).slice(0, 2))
  if (!Number.isFinite(h)) return String(hour)
  const suffix = h < 12 ? 'AM' : 'PM'
  const twelve = h % 12 === 0 ? 12 : h % 12
  return `${twelve} ${suffix}`
}

// toFixed, but a tiny negative that rounds to zero reads "0.0", never "-0.0".
export function toFixedNoNegZero(n, digits = 1) {
  const fixed = n.toFixed(digits)
  return Number(fixed) === 0 ? (0).toFixed(digits) : fixed
}

// Spare agents, one decimal; null (no data) is a dash, never a zero.
export function formatSpare(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—'
  return toFixedNoNegZero(n, 1)
}

export function heatmapSummaryText(report) {
  const s = report?.summary
  if (!s) return ''
  if (s.openHours === 0) return 'No open hours with a schedule to judge in this period.'
  return `${s.shortHours} of ${s.openHours} open hours short, ${s.overHours} over.`
}

// The hover/focus readout for one cell, as plain strings (rendered as text).
export function heatmapTooltipLines(cell) {
  if (!cell) return []
  if (!cell.open) return ['Closed hour: no calls reach the queue.']
  const n = (v) => (typeof v === 'number' ? v.toFixed(1) : '—')
  return [
    `${HEATMAP_STATUS_LABEL[cell.status] ?? 'No schedule data'}: ${formatSpare(cell.spareFte)} spare`,
    `Staffed in queue: ${n(cell.staffedFte)} (phone teams ${n(cell.phoneTeamsFte)} + Nick Email ${n(cell.nickEmailFte)}, counted only up to the chat workload)`,
    `Workload: ${n(cell.workloadFte)} (phone ${n(cell.phoneCalls)} calls, chat ${n(cell.chatContacts)} contacts an hour)`,
  ]
}
